import React, { useMemo, useState } from "react";
import {
    Account,
    AccountGroupHierarchyNode,
} from "@common/types/domains/accounting/account";
import { useEntities } from "@hooks/useEntities";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useWorkspaceAccounts } from "@api/account.api";
import {
    Picker,
    pickerTreeRowPlacements,
} from "@design-system/Picker/Picker.lib";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    AccountGroupingIdentity,
    MultiEntityAccount,
} from "./multiEntityAccountGrouping";
import {
    buildMultiEntityAccountTree,
    flattenMultiEntityAccountTree,
    MultiEntityAccountRow,
} from "./multiEntityAccountTree";
import { useMultiEntityAccounts } from "./useMultiEntityAccounts";
import { MultiEntityAccountComboboxItem } from "./MultiEntityAccountComboboxItem";

/** Drops the rows nested under collapsed group headers. */
const withoutCollapsedDescendants = (
    rows: readonly MultiEntityAccountRow[],
    collapsedGroupKeys: ReadonlySet<string>,
): MultiEntityAccountRow[] => {
    const visibleRows: MultiEntityAccountRow[] = [];
    let collapsedDepth: number | null = null;
    for (const row of rows) {
        if (collapsedDepth !== null && row.depth > collapsedDepth) {
            continue;
        }
        collapsedDepth =
            row.kind === "group" && collapsedGroupKeys.has(row.key)
                ? row.depth
                : null;
        visibleRows.push(row);
    }
    return visibleRows;
};

interface Props {
    selectedAccountName?: string;
    onChange: (account: MultiEntityAccount | null) => void;
    /**
     * Entities to resolve accounts against. When empty/undefined the list is
     * intentionally empty — callers should only render this component once at
     * least one entity is selected.
     */
    selectedEntityIds?: number[];
    container?: HTMLElement;
    accounts?: Array<Account & { entityId: number }>;
    accountsLoading?: boolean;
    coverage?: "any" | "all";
    identity?: AccountGroupingIdentity;
    groupsByEntityId?: Map<number, AccountGroupHierarchyNode[]>;
    groupsLoading?: boolean;
    selectedAccountIds?: string[];
    allowManuallyCreatedAcrossEntities?: boolean;
    hideEntityIcons?: boolean;
    size?: TriggerButtonProps["size"];
    className?: string;
    /** Offers "Clear selection" once an account is picked. Defaults to true. */
    clearable?: boolean;
}

export const MultiEntityAccountCombobox: React.FC<Props> = ({
    selectedAccountName,
    onChange,
    selectedEntityIds,
    container,
    accounts: providedAccounts,
    accountsLoading,
    coverage = "any",
    identity = "nameTypeSubtype",
    groupsByEntityId,
    groupsLoading = false,
    selectedAccountIds,
    allowManuallyCreatedAcrossEntities,
    hideEntityIcons,
    size = "sm",
    className,
    clearable = true,
}) => {
    const [inputValue, setInputValue] = useState("");
    const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<
        ReadonlySet<string>
    >(() => new Set());
    const entities = useEntities();
    const showAccountNumbers = useShowAccountNumbers();
    const workspaceAccounts = useWorkspaceAccounts();
    const accounts = providedAccounts ?? workspaceAccounts.data;
    const isLoading =
        (accountsLoading ?? workspaceAccounts.isLoading) || groupsLoading;

    const accountsByName = useMultiEntityAccounts({
        accounts,
        selectedEntityIds,
        coverage,
        identity,
        groupsByEntityId,
    });

    const accountTree = useMemo(
        () =>
            buildMultiEntityAccountTree({
                accounts: accountsByName,
                identity,
                groupsByEntityId,
            }),
        [accountsByName, identity, groupsByEntityId],
    );

    const sections = useMemo(
        () =>
            flattenMultiEntityAccountTree({
                tree: accountTree,
                search: inputValue,
            }),
        [accountTree, inputValue],
    );

    const isSearching = inputValue.trim() !== "";
    const visibleSections = useMemo(
        () =>
            sections.map((section) => {
                const rows = isSearching
                    ? section.rows
                    : withoutCollapsedDescendants(
                          section.rows,
                          collapsedGroupKeys,
                      );
                return {
                    ...section,
                    rows,
                    placements: pickerTreeRowPlacements(
                        rows.map((row) => row.depth),
                    ),
                };
            }),
        [sections, isSearching, collapsedGroupKeys],
    );
    const toggleGroup = (groupKey: string) =>
        setCollapsedGroupKeys((current) => {
            const next = new Set(current);
            if (!next.delete(groupKey)) {
                next.add(groupKey);
            }
            return next;
        });

    const accountsByKey = useMemo(
        () => new Map(accountsByName.map((account) => [account.key, account])),
        [accountsByName],
    );

    const selectedAccount = useMemo(() => {
        if (selectedAccountIds) {
            const selectedIds = new Set(selectedAccountIds);
            return (
                accountsByName.find(
                    (account) =>
                        account.constituents.length === selectedIds.size &&
                        account.constituents.every((constituent) =>
                            selectedIds.has(constituent.id),
                        ),
                ) ?? null
            );
        }
        return (
            accountsByName.find(
                (account) => account.name === selectedAccountName,
            ) ?? null
        );
    }, [accountsByName, selectedAccountName, selectedAccountIds]);

    const hasSelection = selectedAccount !== null || !!selectedAccountName;

    return (
        <Picker.Select
            searchable
            container={container}
            value={selectedAccount?.key ?? null}
            onValueChange={(key) => {
                const account =
                    key === null ? undefined : accountsByKey.get(key);
                if (account) {
                    onChange(account);
                }
            }}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setInputValue("");
                }
            }}
        >
            <Picker.ButtonTrigger
                render={
                    <Picker.Button
                        variant="primary"
                        size={size ?? "md"}
                        className={className}
                    />
                }
                value={selectedAccountName}
                placeholder="Select account..."
            />
            <Picker.Popup>
                <Picker.Search placeholder="Search accounts…" />
                <Picker.List>
                    {isLoading && <Picker.Loading>Loading…</Picker.Loading>}
                    {!isLoading && sections.length === 0 && (
                        <Picker.Empty>No matching accounts</Picker.Empty>
                    )}
                    {!isLoading && clearable && hasSelection && (
                        <Picker.Item onSelect={() => onChange(null)}>
                            Clear selection
                        </Picker.Item>
                    )}
                    {!isLoading &&
                        visibleSections.map((section, index) => (
                            <React.Fragment key={section.type}>
                                {index > 0 && <Picker.Separator />}
                                <Picker.Section>
                                    <Picker.SectionTitle>
                                        {section.label}
                                    </Picker.SectionTitle>
                                    {section.rows.map((row, rowIndex) => {
                                        const placement =
                                            section.placements[rowIndex];
                                        return row.kind === "group" ? (
                                            <Picker.Item
                                                key={row.key}
                                                depth={row.depth}
                                                treePosition={
                                                    placement.treePosition
                                                }
                                                ancestorLines={
                                                    placement.ancestorLines
                                                }
                                                expandable={!isSearching}
                                                expanded={
                                                    isSearching ||
                                                    !collapsedGroupKeys.has(
                                                        row.key,
                                                    )
                                                }
                                                onExpandedChange={() =>
                                                    toggleGroup(row.key)
                                                }
                                                onSelect={() =>
                                                    toggleGroup(row.key)
                                                }
                                                closeOnSelect={false}
                                            >
                                                {row.label}
                                            </Picker.Item>
                                        ) : (
                                            <MultiEntityAccountComboboxItem
                                                key={row.key}
                                                account={row.account}
                                                entities={entities}
                                                identity={identity}
                                                selectedEntityIds={
                                                    selectedEntityIds
                                                }
                                                allowManuallyCreatedAcrossEntities={
                                                    allowManuallyCreatedAcrossEntities
                                                }
                                                showAccountNumbers={
                                                    showAccountNumbers
                                                }
                                                hideEntityIcons={
                                                    hideEntityIcons
                                                }
                                                depth={row.depth}
                                                treePosition={
                                                    placement.treePosition
                                                }
                                                ancestorLines={
                                                    placement.ancestorLines
                                                }
                                            />
                                        );
                                    })}
                                </Picker.Section>
                            </React.Fragment>
                        ))}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
